import React, { useState, useMemo, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Stack,
    Box,
    Divider,
    TextField,
    MenuItem,
    IconButton,
    Alert,
    CircularProgress,
} from "@mui/material";
import { Product } from "../interfaces/product";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import BlockIcon from "@mui/icons-material/Block";
import { Order } from "../interfaces/Order";
import { useCollection } from "../hooks/useCollection";
import { CreditCustomer } from "../interfaces/CreditCustomer";
import { Worker, WorkersConfig, WorkerTransaction } from "../interfaces/Worker";
import { Users } from "../interfaces/Users";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { InvoiceReceipt } from "./InvoiceReceipt";
import PrintIcon from "@mui/icons-material/Print";
import { groupProducts } from "../global/utils";

interface CheckoutDialogProps {
    open: boolean;
    order: Order | null;
    onClose: () => void;
    onComplete: (order: Order, finalPaymentMethod: string, customer?: { id: string, name: string }) => Promise<void>;
    onCancelOrder?: (order: Order) => Promise<void>;
}

export default function CheckoutDialog({
    open,
    order,
    onClose,
    onComplete,
    onCancelOrder,
}: CheckoutDialogProps) {
    const { restaurantId } = useParams<{ restaurantId: string }>();
    const [isLoading, setIsLoading] = useState(false);
    const isBillingRef = useRef(false);
    const [paymentMethod, setPaymentMethod] = useState<string>("");
    const [cashReceived, setCashReceived] = useState<number | "">("");
    const [cashCurrency, setCashCurrency] = useState<"COP" | "USD">("COP");
    const [selectedWorkerId, setSelectedWorkerId] = useState<string>("");
    const [workersConfig, setWorkersConfig] = useState<WorkersConfig>({ globalMaxDebt: 50000 });

    // Plan Guía
    const [isGuiaPlan, setIsGuiaPlan] = useState<boolean>(false);

    // Tip & Discount States
    const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
    const [tipMode, setTipMode] = useState<"percentage" | "fixed">("percentage");
    const [tipPercentage, setTipPercentage] = useState<number>(0);
    const [tipFixedAmount, setTipFixedAmount] = useState<number | "">("");

    const [discountType, setDiscountType] = useState<"none" | "total" | "producto">("none");
    const [discountMode, setDiscountMode] = useState<"percentage" | "fixed">("percentage");
    const [discountValue, setDiscountValue] = useState<number | "">("");
    const [discountProductIdx, setDiscountProductIdx] = useState<number | "">("");

    const { data: allProducts = [] } = useCollection<Product>(restaurantId!, "products");
    const { data: creditCustomers = [] } = useCollection<CreditCustomer>(
        restaurantId!,
        "credit_customers"
    );
    const { data: workers = [], updateItem: updateWorker, setItem: setWorker } = useCollection<Worker>(
        restaurantId!,
        "workers"
    );
    const { data: usersData = [] } = useCollection<Users>(restaurantId!, "users");

    const combinedWorkers = useMemo(() => {
        const list = [...workers];
        if (usersData) {
            usersData.forEach((user) => {
                const uId = user.uid || (user as any).id;
                if (!uId) return;
                const exists = list.find((w) => w.id === uId);
                if (!exists && user.name) {
                    list.push({
                        id: uId,
                        name: user.name,
                        phone: user.email || "",
                        currentDebt: 0,
                        isActive: true,
                        history: [],
                    });
                }
            });
        }
        return list;
    }, [workers, usersData]);

    const handleCancelOrder = async () => {
        if (!order) return;
        if (!window.confirm(`¿Anular definitivamente la orden "${order.name}"? Esto la marcará como cancelada.`)) return;
        
        setIsLoading(true);
        try {
            if (onCancelOrder) {
                await onCancelOrder(order);
            }
        } catch (error) {
            console.error("Error al anular:", error);
        } finally {
            setIsLoading(false);
            onClose();
        }
    };

    React.useEffect(() => {
        if (!restaurantId) return;
        import("firebase/firestore").then(({ doc, onSnapshot }) => {
            import("../firebase/config").then(({ db }) => {
                const configRef = doc(db, "restaurants", restaurantId, "settings", "workers_config");
                const unsub = onSnapshot(configRef, (docSnap) => {
                    if (docSnap.exists()) {
                        setWorkersConfig(docSnap.data() as WorkersConfig);
                    }
                });
                return () => unsub();
            });
        });
    }, [restaurantId]);

    const [localProducts, setLocalProducts] = useState<any[]>([]);

    const selectedCustomer = useMemo(() =>
        creditCustomers.find(c => c.id === selectedCustomerId),
        [creditCustomers, selectedCustomerId]);

    // Calculate total whenever an order is active
    const subTotal = useMemo(() => {
        return localProducts.reduce(
            (acc, p) => acc + Number(p.total ?? Number(p.price) * Number(p.quantity)),
            0
        );
    }, [localProducts]);

    const guiaDiscount = useMemo(() => {
        if (!isGuiaPlan || !localProducts.length) return 0;
        let foodCount = 0;
        localProducts.forEach(p => {
            const productDef = allProducts.find(prod => prod.id === p.productId);
            const cat = productDef?.category?.toLowerCase() || "";
            const name = p.productName.toLowerCase();

            const isDrink = cat.includes("bebida") || cat.includes("jugo") || cat.includes("gaseosa") ||
                cat.includes("cerveza") || cat.includes("licor") || cat.includes("trago") ||
                name.includes("bebida") || name.includes("jugo") || name.includes("gaseosa") ||
                name.includes("cerveza") || name.includes("limonada") || name.includes("agua");

            if (!isDrink) {
                foodCount += Number(p.quantity || 1);
            }
        });
        return foodCount * 5000;
    }, [isGuiaPlan, localProducts, allProducts]);

    const discountAmount = useMemo(() => {
        let baseDiscount = 0;
        
        // Sum individual product discounts
        localProducts.forEach(p => {
            if (p.discount && p.discount > 0) {
                const targetAmount = Number(p.total ?? (p.price * p.quantity));
                if (p.discountMode === "fixed") {
                    baseDiscount += Number(p.discount);
                } else {
                    baseDiscount += Math.round(targetAmount * (Number(p.discount) / 100));
                }
            }
        });

        // Add global discount if any
        if (discountType !== "none" && discountValue) {
            let targetAmount = subTotal;
            if (discountType === "producto") {
                if (discountProductIdx !== "") {
                    const p = localProducts[Number(discountProductIdx)];
                    if (p) targetAmount = Number(p.total ?? (p.price * p.quantity));
                }
            }
            let extraDiscount = 0;
            if (discountMode === "percentage") {
                extraDiscount = Math.round(targetAmount * (Number(discountValue) / 100));
            } else {
                extraDiscount = Number(discountValue);
            }
            baseDiscount += Math.min(extraDiscount, targetAmount);
        }

        return baseDiscount + guiaDiscount;
    }, [discountType, discountMode, discountValue, discountProductIdx, subTotal, localProducts, guiaDiscount]);

    const tipAmount = useMemo(() => {
        const baseForTip = subTotal - discountAmount;
        if (tipMode === "percentage") return Math.round(baseForTip * (tipPercentage / 100));
        return Number(tipFixedAmount) || 0;
    }, [subTotal, discountAmount, tipPercentage, tipFixedAmount, tipMode]);

    const totalGeneral = subTotal - discountAmount + tipAmount;

    // Pagos Separados (Split Payments)
    const [isSplitPayment, setIsSplitPayment] = useState<boolean>(false);
    const [splitPayments, setSplitPayments] = useState<{ method: string; amount: number | ""; workerId?: string }[]>([
        { method: "efectivo", amount: "" },
        { method: "transferencia", amount: "" }
    ]);

    const splitTotal = useMemo(() => {
        if (!isSplitPayment) return 0;
        return splitPayments.reduce((sum, sp) => sum + (Number(sp.amount) || 0), 0);
    }, [isSplitPayment, splitPayments]);

    const splitRemaining = useMemo(() => {
        if (!isSplitPayment) return 0;
        return totalGeneral - splitTotal;
    }, [isSplitPayment, totalGeneral, splitTotal]);

    const isSplitValid = useMemo(() => {
        if (!isSplitPayment) return true;
        if (splitPayments.length < 2) return false;
        if (Math.abs(splitRemaining) > 0.01) return false;
        return splitPayments.every(sp => Number(sp.amount) > 0 && !!sp.method);
    }, [isSplitPayment, splitPayments, splitRemaining]);

    const handleEqualSplit = (parts: number) => {
        if (parts <= 0 || totalGeneral <= 0) return;
        const partAmount = Math.floor(totalGeneral / parts);
        const remainder = totalGeneral - (partAmount * parts);

        const defaultMethods = ["efectivo", "transferencia", "tarjeta", "trabajador"];
        const newSplits = [];
        for (let i = 0; i < parts; i++) {
            const amt = i === 0 ? partAmount + remainder : partAmount;
            newSplits.push({
                method: defaultMethods[i % defaultMethods.length],
                amount: amt
            });
        }
        setSplitPayments(newSplits);
    };

    React.useEffect(() => {
        if (order) {
            // No pre-llenamos el método de pago para obligar al usuario a seleccionarlo
            setPaymentMethod("");
            setIsSplitPayment(false);
            setSplitPayments([
                { method: "efectivo", amount: "" },
                { method: "transferencia", amount: "" }
            ]);
            // Deep copy and group objects to avoid mutation and unify lines for billing
            if (!order.products || !Array.isArray(order.products)) {
                const pizzas = ((order as any).pizzas || [order]).map((pizzaObj: any, i: number) => {
                    const price = Number(pizzaObj.totalPrice ?? pizzaObj.price ?? order.total ?? (order as any).totalPrice ?? 0);
                    const finalPrice = ((order as any).pizzas && (order as any).pizzas.length > 0 && !pizzaObj.totalPrice && !pizzaObj.price) 
                      ? price / (order as any).pizzas.length 
                      : price;

                    return {
                        productId: `__standalone_${i}__`,
                        productName: `Pizza ${pizzaObj.sizeName || ''}`.trim(),
                        price: finalPrice,
                        quantity: Number(pizzaObj.quantity ?? 1),
                        total: finalPrice * Number(pizzaObj.quantity ?? 1),
                        discountMode: "percentage",
                    };
                });
                setLocalProducts(pizzas);
            } else {
                setLocalProducts(groupProducts(order.products).map(p => ({ 
                    ...p, 
                    discountMode: p.discountMode || "percentage" 
                })));
            }
        } else {
            setLocalProducts([]);
        }
        setTipMode("percentage");
        setTipPercentage(0);
        setTipFixedAmount("");
        setCashReceived("");
        setCashCurrency("COP");
        setDiscountType("none");
        setDiscountMode("percentage");
        setDiscountValue("");
        setDiscountProductIdx("");
        setIsGuiaPlan(false);
        setSelectedCustomerId("");
    }, [order]);

    const handlePriceChange = (idx: number, newPrice: number) => {
        setLocalProducts(prev => {
            const next = [...prev];
            const item = { ...next[idx] };
            item.price = newPrice;
            item.total = newPrice * item.quantity;
            next[idx] = item;
            return next;
        });
    };

    const handleProductDiscountChange = (idx: number, val: number) => {
        setLocalProducts(prev => {
            const next = [...prev];
            const item = { ...next[idx] };
            item.discount = val;
            next[idx] = item;
            return next;
        });
    };

    const handleProductDiscountModeChange = (idx: number, mode: "percentage" | "fixed") => {
        setLocalProducts(prev => {
            const next = [...prev];
            const item = { ...next[idx] };
            item.discountMode = mode;
            next[idx] = item;
            return next;
        });
    };

    // Calculate change
    const changeToGive = useMemo(() => {
        if (paymentMethod !== "efectivo" || cashReceived === "") return 0;
        const amount = Number(cashReceived);
        return amount > totalGeneral ? amount - totalGeneral : 0;
    }, [cashReceived, totalGeneral, paymentMethod]);

    const isShortOnCash =
        paymentMethod === "efectivo" &&
        cashReceived !== "" &&
        Number(cashReceived) < totalGeneral;

    const selectedWorker = useMemo(() => 
        combinedWorkers.find(w => w.id === selectedWorkerId),
    [combinedWorkers, selectedWorkerId]);

    const isWorkerDebtExceeded = false;

    const componentRef = useRef<HTMLDivElement>(null);
    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: `Factura_${order?.id || 'new'}`,
    });

    const handleFinish = async () => {
        if (isSplitPayment) {
            if (!isSplitValid) return;
        } else {
            if (!paymentMethod) return;
            if (isShortOnCash) return;
            if (paymentMethod === "trabajador" && !selectedWorkerId) return;
        }

        // Prevenir doble envío
        if (isBillingRef.current) return;
        isBillingRef.current = true;
        setIsLoading(true);

        try {
            if (order) {
                const customerData = selectedCustomer ? {
                    id: selectedCustomer.id!,
                    name: selectedCustomer.name
                } : selectedWorker ? {
                    id: selectedWorker.id!,
                    name: `Trabajador: ${selectedWorker.name}`
                } : undefined;

                const cleanSplitPayments = isSplitPayment
                    ? splitPayments
                        .filter(sp => Number(sp.amount) > 0)
                        .map(sp => ({ method: sp.method, amount: Number(sp.amount) }))
                    : null;

                const finalPaymentMethod = isSplitPayment ? "pago_separado" : paymentMethod;

                const payload: any = {
                    ...order,
                    products: localProducts,
                    subTotal,
                    discountAmount: discountAmount > 0 ? discountAmount : null,
                    discountType: discountAmount > 0 ? (discountType as "total" | "producto") : null,
                    discountMode: discountAmount > 0 ? discountMode : null,
                    discountValue: discountAmount > 0 ? Number(discountValue) : null,
                    discountProductId: discountAmount > 0 && discountType === "producto" && discountProductIdx !== ""
                        ? localProducts[Number(discountProductIdx)].productId
                        : null,
                    isGuiaPlan,
                    guiaDiscount: guiaDiscount > 0 ? guiaDiscount : null,
                    tipPercentage,
                    tipAmount,
                    total: totalGeneral,
                    customer: customerData,
                    // Multi-currency cash
                    cashCurrency: paymentMethod === "efectivo" ? cashCurrency : null,
                    isSplitPayment: isSplitPayment,
                    splitPayments: cleanSplitPayments,
                    isPaid: true,
                    paymentStatus: "paid",
                };

                Object.keys(payload).forEach(key => {
                    if (payload[key] === undefined || payload[key] === null) {
                        delete payload[key];
                    }
                });

                await onComplete(payload as Order, finalPaymentMethod, customerData);

                if (isSplitPayment) {
                    for (const sp of cleanSplitPayments || []) {
                        if (sp.method === "trabajador" && selectedWorker) {
                            const newTx: WorkerTransaction = {
                                id: Date.now().toString() + Math.random().toString(36).substring(7),
                                type: 'compra',
                                amount: sp.amount,
                                date: Date.now(),
                                detail: `Orden (Pago separado): ${localProducts.map(p => `${p.quantity}x ${p.productName}`).join(", ")}`
                            };
                            const existingWorker = workers.find(w => w.id === selectedWorker.id);
                            if (existingWorker) {
                                await updateWorker(selectedWorker.id!, {
                                    currentDebt: selectedWorker.currentDebt + sp.amount,
                                    history: [...(selectedWorker.history || []), newTx]
                                });
                            }
                        }
                    }
                } else if (paymentMethod === "trabajador" && selectedWorker) {
                    const newTx: WorkerTransaction = {
                        id: Date.now().toString() + Math.random().toString(36).substring(7),
                        type: 'compra',
                        amount: totalGeneral,
                        date: Date.now(),
                        detail: `Orden: ${localProducts.map(p => `${p.quantity}x ${p.productName}`).join(", ")}`
                    };
                    const existingWorker = workers.find(w => w.id === selectedWorker.id);
                    if (existingWorker) {
                        await updateWorker(selectedWorker.id!, {
                            currentDebt: selectedWorker.currentDebt + totalGeneral,
                            history: [...(selectedWorker.history || []), newTx]
                        });
                    } else {
                        await setWorker(selectedWorker.id!, {
                            name: selectedWorker.name,
                            phone: selectedWorker.phone || "",
                            isActive: selectedWorker.isActive ?? true,
                            currentDebt: selectedWorker.currentDebt + totalGeneral,
                            history: [...(selectedWorker.history || []), newTx]
                        });
                    }
                }

                onClose();
            }
        } catch (err) {
            console.error("Error al facturar:", err);
            alert("Ocurrió un error al facturar la orden. Por favor intenta de nuevo.");
        } finally {
            setIsLoading(false);
            isBillingRef.current = false;
        }
    };

    if (!order) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
                    <Typography variant="h5" fontWeight={800} display="flex" alignItems="center" gap={1} sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}>
                        <PointOfSaleIcon color="primary" fontSize="large" />
                        Checkout & Facturación
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: "100%", sm: "auto" }, justifyContent: "space-between" }}>
                        <Button
                            variant="outlined"
                            size="small"
                            color="info"
                            startIcon={<PrintIcon />}
                            onClick={() => handlePrint()}
                            sx={{ borderRadius: 2, fontWeight: 900, px: 2 }}
                        >
                            Imprimir Factura
                        </Button>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ pb: 3 }}>
                <Box mb={2} mt={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Orden a cobrar:
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                        {order.name}
                    </Typography>
                </Box>



                {/* Selector de Propina */}
                <Box mb={3}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1} fontWeight={600}>
                        Agregar Propina:
                    </Typography>

                    <Stack direction="row" spacing={1} mb={1.5}>
                        <Button
                            variant={tipMode === "percentage" ? "contained" : "outlined"}
                            size="small"
                            onClick={() => setTipMode("percentage")}
                            sx={{ borderRadius: 2 }}
                        >
                            Porcentaje (%)
                        </Button>
                        <Button
                            variant={tipMode === "fixed" ? "contained" : "outlined"}
                            size="small"
                            onClick={() => setTipMode("fixed")}
                            sx={{ borderRadius: 2 }}
                        >
                            Monto Fijo ($)
                        </Button>
                    </Stack>

                    {tipMode === "percentage" ? (
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                            {[0, 5, 10, 15].map((pct) => (
                                <Button
                                    key={pct}
                                    variant={tipPercentage === pct ? "contained" : "outlined"}
                                    onClick={() => setTipPercentage(pct)}
                                    size="small"
                                    sx={{ borderRadius: 2, minWidth: '60px' }}
                                >
                                    {pct}%
                                </Button>
                            ))}
                            <TextField
                                size="small"
                                type="number"
                                placeholder="Otro"
                                value={![0, 5, 10, 15].includes(tipPercentage) ? tipPercentage || '' : ""}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    if (val >= 0) setTipPercentage(val);
                                }}
                                InputProps={{
                                    endAdornment: <Typography variant="caption" sx={{ ml: 0.5 }}>%</Typography>,
                                    sx: { width: 90, borderRadius: 2, height: 32, fontSize: '0.875rem' }
                                }}
                            />
                        </Stack>
                    ) : (
                        <TextField
                            size="small"
                            type="number"
                            placeholder="Escribe el monto..."
                            value={tipFixedAmount}
                            onChange={(e) => {
                                const val = Number(e.target.value);
                                setTipFixedAmount(val >= 0 ? val : "");
                            }}
                            fullWidth
                            InputProps={{
                                startAdornment: <Typography variant="caption" sx={{ mr: 1, fontWeight: 800 }}>$</Typography>,
                                sx: { borderRadius: 2 }
                            }}
                        />
                    )}
                </Box>

                {/* Selector de Promociones y Descuentos */}
                <Box mb={3}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1} fontWeight={600}>
                        Promociones y Descuentos:
                    </Typography>

                    <TextField
                        select
                        size="small"
                        value={discountType}
                        onChange={(e) => {
                            setDiscountType(e.target.value as any);
                            setDiscountValue("");
                            setDiscountProductIdx("");
                        }}
                        fullWidth
                        sx={{ mb: 2 }}
                    >
                        <MenuItem value="none">Sin descuento</MenuItem>
                        <MenuItem value="total">Descuento al Total de la Orden</MenuItem>
                        <MenuItem value="producto">Descuento a un Producto Específico</MenuItem>
                    </TextField>

                    {discountType !== "none" && (
                        <Box sx={{ p: 2, bgcolor: "#f1f5f9", borderRadius: 2 }}>
                            {discountType === "producto" && (
                                <TextField
                                    select
                                    label="Seleccionar Producto"
                                    size="small"
                                    value={discountProductIdx}
                                    onChange={(e) => setDiscountProductIdx(e.target.value as any)}
                                    fullWidth
                                    sx={{ mb: 2, bgcolor: "#fff" }}
                                >
                                    {localProducts.map((p, idx) => (
                                        <MenuItem key={idx} value={idx}>
                                            {p.quantity}x {p.productName} (${Number(p.total ?? (p.price * p.quantity)).toLocaleString()})
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}

                            <Stack direction="row" spacing={1} mb={2}>
                                <Button
                                    variant={discountMode === "percentage" ? "contained" : "outlined"}
                                    size="small"
                                    onClick={() => setDiscountMode("percentage")}
                                    sx={{ borderRadius: 2 }}
                                    color="secondary"
                                >
                                    Porcentaje (%)
                                </Button>
                                <Button
                                    variant={discountMode === "fixed" ? "contained" : "outlined"}
                                    size="small"
                                    onClick={() => setDiscountMode("fixed")}
                                    sx={{ borderRadius: 2 }}
                                    color="secondary"
                                >
                                    Monto Fijo ($)
                                </Button>
                            </Stack>

                            <TextField
                                size="small"
                                type="number"
                                placeholder={discountMode === "percentage" ? "Ej. 10" : "Ej. 5000"}
                                value={discountValue}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setDiscountValue(val >= 0 ? val : "");
                                }}
                                fullWidth
                                sx={{ bgcolor: "#fff" }}
                                InputProps={{
                                    endAdornment: discountMode === "percentage" ? <Typography variant="caption" sx={{ ml: 0.5 }}>%</Typography> : null,
                                    startAdornment: discountMode === "fixed" ? <Typography variant="caption" sx={{ mr: 1, fontWeight: 800 }}>$</Typography> : null,
                                    sx: { borderRadius: 2 }
                                }}
                            />
                        </Box>
                    )}
                </Box>

                {/* Ajuste de Precios */}
                <Box mb={3}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1} fontWeight={600}>
                        Ajuste de Precios de Productos:
                    </Typography>
                    <Stack spacing={1.5}>
                        {localProducts.map((p, idx) => (
                            <Box key={idx} sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={1.5}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight={800}>
                                            {p.quantity}x {p.productName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {p.variantLabel && p.variantLabel !== "Única" ? p.variantLabel : ""}
                                        </Typography>
                                    </Box>
                                    <TextField
                                        label="Precio Unit."
                                        size="small"
                                        type="number"
                                        value={p.price}
                                        onChange={(e) => handlePriceChange(idx, Number(e.target.value))}
                                        sx={{ width: 130, bgcolor: "#fff" }}
                                        InputProps={{
                                            startAdornment: <Typography variant="caption" sx={{ mr: 0.5, fontWeight: 700 }}>$</Typography>,
                                            sx: { borderRadius: 1.5, fontWeight: 800 }
                                        }}
                                    />
                                </Stack>
                                <Divider sx={{ my: 1.5, borderStyle: 'dotted' }} />
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="caption" fontWeight={700} sx={{ minWidth: 70 }}>Desc. Ítem:</Typography>
                                    <Stack direction="row" spacing={0.5}>
                                        <Button
                                            variant={p.discountMode === "fixed" ? "outlined" : "contained"}
                                            size="small"
                                            onClick={() => handleProductDiscountModeChange(idx, "percentage")}
                                            sx={{ minWidth: 32, px: 1, height: 28, fontSize: '0.7rem' }}
                                        >
                                            %
                                        </Button>
                                        <Button
                                            variant={p.discountMode === "fixed" ? "contained" : "outlined"}
                                            size="small"
                                            onClick={() => handleProductDiscountModeChange(idx, "fixed")}
                                            sx={{ minWidth: 32, px: 1, height: 28, fontSize: '0.7rem' }}
                                        >
                                            $
                                        </Button>
                                    </Stack>
                                    <TextField
                                        size="small"
                                        type="number"
                                        placeholder="0"
                                        value={p.discount || ""}
                                        onChange={(e) => handleProductDiscountChange(idx, Number(e.target.value))}
                                        sx={{ flex: 1, bgcolor: "#fff" }}
                                        InputProps={{
                                            sx: { borderRadius: 1.5, height: 32, fontSize: '0.85rem' }
                                        }}
                                    />
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </Box>

                {/* Resumen de Total */}
                <Box
                    sx={{
                        bgcolor: "#f8fafc",
                        p: 3,
                        borderRadius: 3,
                        border: "2px solid #e2e8f0",
                        mb: 3,
                    }}
                >
                    <Stack spacing={1} mb={2}>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography color="text.secondary" variant="body2" fontWeight={600}>Subtotal ({localProducts.length} productos)</Typography>
                            <Typography fontWeight={700} variant="body2">${subTotal.toLocaleString("es-CO")}</Typography>
                        </Stack>

                        {discountAmount > 0 && (
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography color="text.secondary" variant="body2" fontWeight={600}>
                                    Descuento {discountMode === "percentage" && `(${discountValue}%)`} {discountType === "producto" && `- Prod`}
                                </Typography>
                                <Typography fontWeight={800} variant="body2" color="error.main">- ${discountAmount.toLocaleString("es-CO")}</Typography>
                            </Stack>
                        )}

                        {tipAmount > 0 && (
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography color="text.secondary" variant="body2" fontWeight={600}>
                                    Propina {tipMode === "percentage" && `(${tipPercentage}%)`}
                                </Typography>
                                <Typography fontWeight={700} variant="body2" color="success.main">+ ${tipAmount.toLocaleString("es-CO")}</Typography>
                            </Stack>
                        )}
                    </Stack>
                    <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />
                    <Box sx={{ textAlign: "center" }}>
                        <Typography variant="overline" color="text.secondary" fontWeight={800}>
                            TOTAL A PAGAR
                        </Typography>
                        <Typography variant="h3" fontWeight={900} color="primary.main">
                            ${totalGeneral.toLocaleString("es-CO")}
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Stack spacing={2.5}>
                    {/* Selector de Modo de Pago: Único vs Separado */}
                    <Stack direction="row" spacing={1} bgcolor="#f1f5f9" p={0.5} borderRadius={3}>
                        <Button
                            variant={!isSplitPayment ? "contained" : "text"}
                            color={!isSplitPayment ? "primary" : "inherit"}
                            onClick={() => {
                                setIsSplitPayment(false);
                                setPaymentMethod("");
                            }}
                            fullWidth
                            sx={{ borderRadius: 2.5, fontWeight: 800, textTransform: "none" }}
                        >
                            💵 Pago Único
                        </Button>
                        <Button
                            variant={isSplitPayment ? "contained" : "text"}
                            color={isSplitPayment ? "secondary" : "inherit"}
                            onClick={() => {
                                setIsSplitPayment(true);
                                setPaymentMethod("pago_separado");
                            }}
                            fullWidth
                            sx={{ borderRadius: 2.5, fontWeight: 800, textTransform: "none" }}
                        >
                            ✂️ Cuentas Separadas / Dividido
                        </Button>
                    </Stack>

                    {!isSplitPayment ? (
                        <>
                            {/* Selector de Método de Pago Único */}
                            <TextField
                                select
                                label="Seleccionar Método de Pago"
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                fullWidth
                                variant="outlined"
                                required
                                error={!paymentMethod}
                                helperText={!paymentMethod ? "Debes seleccionar un método de pago" : ""}
                            >
                                <MenuItem value="" disabled>--- Selecciona un método ---</MenuItem>
                                <MenuItem value="efectivo">💵 Efectivo</MenuItem>
                                <MenuItem value="tarjeta">💳 Tarjeta de Crédito/Débito</MenuItem>
                                <MenuItem value="transferencia">🏦 Transferencia / Nequi</MenuItem>
                                <MenuItem value="trabajador">👨‍🔧 Trabajador (Crédito)</MenuItem>
                            </TextField>

                            {/* Calculadora de Efectivo */}
                            {paymentMethod === "efectivo" && (
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: "#fffbeb",
                                        borderRadius: 3,
                                        border: "1px dashed #f59e0b",
                                    }}
                                >
                                    <Typography variant="subtitle2" fontWeight={800} color="#b45309" mb={2}>
                                        Calculadora de Cambio
                                    </Typography>

                                    <Stack direction="row" spacing={2} alignItems="flex-start">
                                        <TextField
                                            label="Efectivo Recibido ($)"
                                            value={cashReceived !== "" ? Number(cashReceived).toLocaleString("es-CO") : ""}
                                            onChange={(e) => {
                                                const rawDigits = e.target.value.replace(/\D/g, "");
                                                setCashReceived(rawDigits === "" ? "" : Number(rawDigits));
                                            }}
                                            error={isShortOnCash}
                                            helperText={isShortOnCash ? "Monto insuficiente" : ""}
                                            fullWidth
                                            InputProps={{ sx: { bgcolor: "#fff", fontWeight: 700 } }}
                                        />
                                        <Box
                                            sx={{
                                                minWidth: '120px',
                                                p: 1.5,
                                                bgcolor: cashReceived ? (isShortOnCash ? '#fee2e2' : '#dcfce7') : '#f1f5f9',
                                                borderRadius: 2,
                                                textAlign: 'center',
                                                border: '1px solid',
                                                borderColor: cashReceived ? (isShortOnCash ? '#ef4444' : '#22c55e') : '#cbd5e1',
                                            }}
                                        >
                                            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">
                                                {isShortOnCash ? "DEBE" : "CAMBIO"}
                                            </Typography>
                                            <Typography variant="h6" fontWeight={900} color={isShortOnCash ? "error.main" : "success.main"}>
                                                ${isShortOnCash
                                                    ? (totalGeneral - Number(cashReceived)).toLocaleString("es-CO")
                                                    : changeToGive.toLocaleString("es-CO")}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}

                            {paymentMethod === "trabajador" && (
                                <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
                                    <Typography variant="subtitle2" fontWeight={800} color="text.secondary" mb={2}>
                                        Seleccionar Trabajador
                                    </Typography>
                                    <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        value={selectedWorkerId}
                                        onChange={(e) => setSelectedWorkerId(e.target.value)}
                                        sx={{ mb: 2, bgcolor: "#fff" }}
                                    >
                                        <MenuItem value="" disabled>--- Selecciona un trabajador ---</MenuItem>
                                        {combinedWorkers.map(w => (
                                            <MenuItem key={w.id} value={w.id!}>
                                                {w.name} (Deuda: ${w.currentDebt.toLocaleString("es-CO")})
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    {selectedWorker && (
                                        <Box sx={{ p: 2, bgcolor: "#fff", borderRadius: 2, border: "1px solid #cbd5e1" }}>
                                            <Stack direction="row" justifyContent="space-between" mb={1}>
                                                <Typography variant="body2" color="text.secondary" fontWeight={700}>Deuda Actual</Typography>
                                                <Typography variant="body2" fontWeight={700}>${selectedWorker.currentDebt.toLocaleString("es-CO")}</Typography>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between" mb={1}>
                                                <Typography variant="body2" color="text.secondary" fontWeight={700}>Nueva Orden</Typography>
                                                <Typography variant="body2" fontWeight={700}>+ ${totalGeneral.toLocaleString("es-CO")}</Typography>
                                            </Stack>
                                            <Divider sx={{ my: 1 }} />
                                            <Stack direction="row" justifyContent="space-between" mb={1}>
                                                <Typography variant="body2" color="text.primary" fontWeight={800}>Deuda Proyectada</Typography>
                                                <Typography variant="body2" color="text.primary" fontWeight={800}>${(selectedWorker.currentDebt + totalGeneral).toLocaleString("es-CO")}</Typography>
                                            </Stack>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {paymentMethod !== "efectivo" && paymentMethod !== "trabajador" && (
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    Verifica el comprobante o los detalles antes de finalizar la orden.
                                </Alert>
                            )}
                        </>
                    ) : (
                        /* FORMULARIO DE PAGO SEPARADO / DIVIDIDO */
                        <Box sx={{ p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: "1.5px solid #cbd5e1" }}>
                            <Typography variant="subtitle2" fontWeight={800} color="text.primary" mb={1}>
                                Desglose de Cuentas Separadas:
                            </Typography>

                            {/* Accesos rápidos para dividir */}
                            <Stack direction="row" spacing={1} alignItems="center" mb={2} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    Dividir equitativamente:
                                </Typography>
                                <Button size="small" variant="outlined" onClick={() => handleEqualSplit(2)} sx={{ borderRadius: 1.5, py: 0.2, fontWeight: 700 }}>
                                    2 partes
                                </Button>
                                <Button size="small" variant="outlined" onClick={() => handleEqualSplit(3)} sx={{ borderRadius: 1.5, py: 0.2, fontWeight: 700 }}>
                                    3 partes
                                </Button>
                                <Button size="small" variant="outlined" onClick={() => handleEqualSplit(4)} sx={{ borderRadius: 1.5, py: 0.2, fontWeight: 700 }}>
                                    4 partes
                                </Button>
                            </Stack>

                            <Stack spacing={1.5} mb={2}>
                                {splitPayments.map((sp, idx) => (
                                    <Stack key={idx} direction="row" spacing={1} alignItems="center">
                                        <TextField
                                            select
                                            size="small"
                                            label={`Método #${idx + 1}`}
                                            value={sp.method}
                                            onChange={(e) => {
                                                const newSplits = [...splitPayments];
                                                newSplits[idx].method = e.target.value;
                                                setSplitPayments(newSplits);
                                            }}
                                            sx={{ width: 170, bgcolor: "#fff" }}
                                        >
                                            <MenuItem value="efectivo">💵 Efectivo</MenuItem>
                                            <MenuItem value="tarjeta">💳 Tarjeta</MenuItem>
                                            <MenuItem value="transferencia">🏦 Transferencia/Nequi</MenuItem>
                                            <MenuItem value="trabajador">👨‍🔧 Trabajador</MenuItem>
                                        </TextField>

                                        <TextField
                                            size="small"
                                            label="Monto ($)"
                                            value={sp.amount !== "" ? Number(sp.amount).toLocaleString("es-CO") : ""}
                                            onChange={(e) => {
                                                const rawDigits = e.target.value.replace(/\D/g, "");
                                                const val = rawDigits === "" ? "" : Number(rawDigits);
                                                const newSplits = [...splitPayments];
                                                newSplits[idx].amount = val;
                                                setSplitPayments(newSplits);
                                            }}
                                            sx={{ flex: 1, bgcolor: "#fff" }}
                                            InputProps={{
                                                startAdornment: <Typography variant="caption" sx={{ mr: 0.5, fontWeight: 800 }}>$</Typography>,
                                                sx: { fontWeight: 800, borderRadius: 1.5 }
                                            }}
                                        />

                                        {splitPayments.length > 2 && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => {
                                                    setSplitPayments(splitPayments.filter((_, i) => i !== idx));
                                                }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Stack>
                                ))}
                            </Stack>

                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                    setSplitPayments([...splitPayments, { method: "efectivo", amount: "" }]);
                                }}
                                sx={{ mb: 2, fontWeight: 700, borderRadius: 2 }}
                            >
                                + Agregar otro pago
                            </Button>

                            {/* Resumen de cuadre de cuentas */}
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 2.5,
                                    bgcolor: isSplitValid ? "#dcfce7" : "#fee2e2",
                                    border: "1px solid",
                                    borderColor: isSplitValid ? "#22c55e" : "#ef4444",
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="body2" color="text.secondary" fontWeight={700}>Total Orden:</Typography>
                                    <Typography variant="body2" fontWeight={800}>${totalGeneral.toLocaleString("es-CO")}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="body2" color="text.secondary" fontWeight={700}>Suma de Pagos Divididos:</Typography>
                                    <Typography variant="body2" fontWeight={800}>${splitTotal.toLocaleString("es-CO")}</Typography>
                                </Stack>
                                <Divider sx={{ my: 0.8, borderStyle: "dashed" }} />
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2" fontWeight={850} color={isSplitValid ? "success.dark" : "error.dark"}>
                                        {splitRemaining === 0 ? "✓ Cuentas cuadradas exactamente" : splitRemaining > 0 ? `Faltan $${splitRemaining.toLocaleString("es-CO")} por asignar` : `Excede el total por $${Math.abs(splitRemaining).toLocaleString("es-CO")}`}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0, justifyContent: "space-between" }}>
                <Stack direction="row" spacing={1}>
                    <Button onClick={onClose} size="large" sx={{ fontWeight: 600 }}>
                        Cancelar
                    </Button>
                    {onCancelOrder && (
                        <Button
                            size="large"
                            color="error"
                            onClick={handleCancelOrder}
                            startIcon={<BlockIcon />}
                            sx={{ fontWeight: 600 }}
                            disabled={isLoading}
                        >
                            Anular Orden
                        </Button>
                    )}
                </Stack>
                <Button
                    variant="contained"
                    size="large"
                    color="success"
                    disabled={isLoading || (!isSplitPayment ? (!paymentMethod || isShortOnCash || (paymentMethod === "trabajador" && !selectedWorkerId)) : !isSplitValid)}
                    onClick={handleFinish}
                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <ReceiptLongIcon />}
                    sx={{
                        fontWeight: 800,
                        py: 1.5,
                        px: 4,
                        borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(16, 185, 129, 0.4)",
                        minWidth: 220,
                    }}
                >
                    {isLoading ? "Procesando..." : "COBRAR Y FACTURAR"}
                </Button>
            </DialogActions>

            {/* Hidden Receipt for Printing */}
            <Box sx={{ display: 'none' }}>
                <InvoiceReceipt
                    ref={componentRef}
                    order={{
                        ...order,
                        products: localProducts,
                        subTotal,
                        discountAmount,
                        tipAmount,
                        total: totalGeneral,
                        isGuiaPlan,
                        guiaDiscount,
                        paymentMethod
                    }}
                />
            </Box>
        </Dialog>
    );
}
